/**
 * ①素材のアップロード。新規登録（NewDish）と差し替え（DishMaterials）で共用する。
 *
 * 動画は 2GB を超えうる（実測 2.1GB）ので resumable upload で直接 GCS へ送る。
 * Cloud Functions を経由させない（関数のペイロード上限に当たるため）。
 *
 * **2箇所に書き分けない。** 拡張子を失ったファイルの contentType 補完（#55）と
 * 保存名の安全化は、片方だけ直すと「新規は通るのに差し替えで弾かれる」になる。
 */

import { ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../firebase";
import { VIDEO_TYPE_ERROR, videoContentType } from "./media";
import { materialStoragePath, type MaterialKind } from "./materials";

export interface UploadProgress {
  name: string;
  transferred: number;
  total: number;
  done: boolean;
}

/**
 * 1ファイルを品目の素材として置き、保存先のパスを返す。
 *
 * @param onProgress 保存先パスをキーに進捗を通知する
 */
export async function uploadMaterial(
  dishId: string,
  file: File,
  kind: MaterialKind,
  onProgress: (path: string, p: UploadProgress) => void,
): Promise<string> {
  // 動画は拡張子を失っていても受ける（「…MP4 のコピー」等。#55 の実運用で判明）
  const contentType = kind === "video" ? videoContentType(file) : file.type;
  if (kind === "video" && !contentType) throw new Error(VIDEO_TYPE_ERROR);

  const path = materialStoragePath(dishId, kind, file.name, Date.now());
  const task = uploadBytesResumable(ref(storage, path), file, {
    contentType: contentType || "application/octet-stream",
  });
  onProgress(path, { name: file.name, transferred: 0, total: file.size, done: false });

  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (s) =>
        onProgress(path, {
          name: file.name,
          transferred: s.bytesTransferred,
          total: s.totalBytes,
          done: false,
        }),
      reject,
      () => {
        onProgress(path, { name: file.name, transferred: file.size, total: file.size, done: true });
        resolve();
      },
    );
  });
  return path;
}

export function mb(n: number): string {
  return `${(n / 1024 / 1024).toFixed(0)}MB`;
}
